import React, { useState, useEffect, SyntheticEvent } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useQuery } from "@apollo/react-hooks";

import Contact from "components/Contact";
import { Button, Input } from "styles";
import { Contact as Person } from "types";
import isEmpty from "utils/isEmpty";
import { YEAR } from "queries";

interface ContactsProps {
  contacts: Array<Person>;
  slug?: string;
  id?: string;
}

function Contacts({ contacts, slug, id }: ContactsProps) {
  const [filteredContacts, setFilteredContacts] = useState<Array<Person>>([]);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("");
  const [isFromLink, setIsFromLink] = useState(false);
  const [selectedYear, setSelectedYear] = useState("Year");
  const history = useHistory();

  const { data: { years } = { years: [] } } = useQuery(YEAR);

  const uniqueYears = ["Year", ...Object.keys(years)];

  const filterContacts = (
    contacts: Array<Person>,
    name: string,
    value: string | number
  ) => contacts.filter((contact: Person) => contact[name] === value);

  useEffect(() => {
    switch (slug) {
      case "age":
        setFilteredContacts(filterContacts(contacts, "age", +id));
        break;
      case "age-gap":
        setFilteredContacts(
          contacts.filter((contact: Person) => {
            const gap = contact.age - (contact.year - 1977);
            return gap === +id;
          })
        );
        break;
      case "country":
        setFilteredContacts(filterContacts(contacts, "country", id));
        break;
      case "ethnicity":
        setFilteredContacts(filterContacts(contacts, "ethnicity", id));
        break;
      case "source":
        setFilteredContacts(filterContacts(contacts, "source", id));
        break;
      case "year":
        setFilteredContacts(filterContacts(contacts, "year", +id));
        break;
      default:
        setFilteredContacts(contacts);
    }

    setIsFromLink(!!slug);
  }, [contacts, id, slug]);

  const handleAgeClick = (age: number) => {
    setFilteredContacts(
      contacts.filter((contact: Person) => contact.age === age)
    );
    setIsFiltered(true);
  };

  const handleAgeGapClick = (ageGap: number) => {
    setFilteredContacts(
      contacts.filter((contact: Person) => {
        const gap = contact.age - (contact.year - 1977);

        return gap === ageGap;
      })
    );
    setIsFiltered(true);
  };

  const handleCountryClick = (ethnicity: string) => {
    setFilteredContacts(filterContacts(contacts, "ethnicity", ethnicity));
    setIsFiltered(true);
  };

  const handleSourceClick = (source: string) => {
    setFilteredContacts(filterContacts(contacts, "source", source));
    setIsFiltered(true);
  };

  const handleFirstNameClick = (first: string) => {
    setFilteredContacts(filterContacts(contacts, "first", first));
    setIsFiltered(true);
  };

  const handleLastNameClick = (last: string) => {
    setFilteredContacts(filterContacts(contacts, "last", last));
    setIsFiltered(true);
  };

  const handleYearChange = (evt: SyntheticEvent) => {
    const year = (evt.target as HTMLInputElement).value;

    setFilteredContacts(
      contacts.filter((contact: Person) => {
        if (year === "Year") {
          return contacts;
        }
        return contact.year === +year;
      })
    );
    setIsFiltered(true);
    setSelectedYear(year);
    setFilter("");
  };

  const handleBackClick = () => {
    setFilteredContacts(contacts);
    setIsFiltered(false);
  };

  const handleHistoryClick = () => {
    history.goBack();
  };

  const handleFilterChange = (evt: SyntheticEvent) => {
    const value = (evt.target as HTMLInputElement).value;
    const filtered = contacts.filter((contact) => {
      const name = `${contact.first} ${contact.middle} ${contact.last}`;
      return name.includes(value);
    });

    setFilteredContacts(filtered);
    setIsFiltered(false);
    setSelectedYear("Year");
    setFilter(value);
  };

  const handleClearClick = (evt: SyntheticEvent) => {
    setFilteredContacts(contacts);
    setIsFiltered(false);
    setSelectedYear("Year");
    setFilter("");
  };

  let previousYear: number;
  let countPerYear: number;

  return (
    <Wrapper>
      {!isFromLink && (
        <div>
          <Label htmlFor="filter">Search</Label>
          <Input
            type="text"
            name="filter"
            value={filter}
            placeholder="Name"
            onChange={handleFilterChange}
          />
          <ClearButton onClick={handleClearClick}>Clear</ClearButton>
          <span>Or</span>
          <Select onChange={handleYearChange} value={selectedYear}>
            {uniqueYears.map((year) => (
              <option value={year}>{year}</option>
            ))}
          </Select>
        </div>
      )}
      {!isEmpty(contacts) && <Total>{filteredContacts.length}</Total>}
      <TABLE>
        <tbody>
          {!isEmpty(filteredContacts.length) && (
            <TR>
              <TH>Year</TH>
              <TH>Month</TH>
              <TH>Ethnicity</TH>
              <TH>Name</TH>
              <TH>Count</TH>
              <TH>Age</TH>
              <TH>Source</TH>
              <TH>Facebook</TH>
            </TR>
          )}
          {filteredContacts.map((contact: Person) => {
            const showYear = previousYear !== contact.year;
            if (showYear) {
              previousYear = contact.year;
              countPerYear = 0;
            }

            return (
              <Contact
                key={contact.id}
                contact={contact}
                showYear={showYear}
                countPerYear={++countPerYear}
                onAgeClick={handleAgeClick}
                onAgeGapClick={handleAgeGapClick}
                onCountryClick={handleCountryClick}
                onSourceClick={handleSourceClick}
                onFirstNameClick={handleFirstNameClick}
                onLastNameClick={handleLastNameClick}
              />
            );
          })}
        </tbody>
      </TABLE>
      {isFiltered && !isFromLink && <ArrowLeft onClick={handleBackClick} />}
      {isFromLink && <ArrowLeft onClick={handleHistoryClick} />}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin: 0;
`;

const TABLE = styled.table`
  padding-left: 0;
  margin-top: 1rem;
`;

const TR = styled.tr``;

const TH = styled.th`
  color: grey;
  font-weight: normal;
  font-size: 0.6rem;
  text-transform: uppercase;
`;

const Total = styled.div`
  font-size: 2rem;
`;

const Label = styled.label.attrs({ className: "contacts-label" })`
  margin-right: 0.5rem;
`;

const Select = styled.select`
  border: 1px solid #dddfe2;
  border-radius: 0.2rem;
  margin: 1rem 0 1rem 0.5rem;
  padding: 0.5rem;
`;

const ClearButton = styled(Button)`
  margin: 0 0.5rem;
`;

const ArrowLeft = styled(FaArrowLeft)`
  cursor: pointer;
`;

export default Contacts;
